import { Button, IconButton, Tag, useAlert } from '@ads/components-react';

import { XMarkIcon } from '@heroicons/react/24/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getApiV1ExamLangsExamlangidItemsQueryKey,
  usePostApiV1ExamImportExamItemBatch,
} from '@/shared/api/generated';

import type { SelectableQuestion } from '../../../hooks/useExamQuestionSelection';
import { ExamQuestionSelectionStep } from '../ExamQuestionSelectionStep';

export interface ImportFromLibraryModalProps {
  readonly open: boolean;
  readonly examLangId: number;
  readonly existingQuestionIds: readonly number[];
  /** Idioma pré-selecionado na tab ao abrir o modal (ex: 'pt-BR', 'en-US') */
  readonly initialCultureInfo?: string;
  /** CultureInfos dos idiomas configurados na prova — para validar balanceamento */
  readonly examCultureInfos?: string[];
  /** Mapa de examLangId por cultureInfo — necessário para importar por idioma */
  readonly examLangIdByCultureInfo?: Record<string, number>;
  readonly onImport: (questions: readonly SelectableQuestion[]) => void;
  readonly onClose: () => void;
}

/**
 * Modal de importação de questões da biblioteca para um idioma de prova.
 *
 * Reutiliza o `ExamQuestionSelectionStep` (filtros + tabela paginada com
 * checkboxes) e confirma a seleção via botão "Importar".
 *
 * @param open - Controla visibilidade do modal
 * @param examLangId - ID do idioma de prova destino
 * @param existingQuestionIds - IDs já presentes na prova (referência futura)
 * @param examCultureInfos - Idiomas configurados na prova para validação de balanceamento
 * @param examLangIdByCultureInfo - Mapa cultureInfo → examLangId para importação por idioma
 * @param onImport - Callback com as questões confirmadas
 * @param onClose - Callback para fechar sem importar
 */
export function ImportFromLibraryModal({
  open,
  examLangId,
  initialCultureInfo,
  examCultureInfos = [],
  examLangIdByCultureInfo = {},
  onImport,
  onClose,
}: ImportFromLibraryModalProps) {
  const { t } = useTranslation('examManagement');
  const { alert } = useAlert();
  const queryClient = useQueryClient();
  const [selectedQuestions, setSelectedQuestions] = useState<SelectableQuestion[]>([]);

  const isBalanced = useMemo(() => {
    if (examCultureInfos.length <= 1) return true;
    const counts = examCultureInfos.map(
      lang => selectedQuestions.filter(q => q.cultureInfo === lang).length,
    );
    const nonZero = counts.filter(c => c > 0);
    return (
      nonZero.length === 0 ||
      (nonZero.every(c => c === nonZero[0]) && nonZero.length === examCultureInfos.length)
    );
  }, [examCultureInfos, selectedQuestions]);

  const importMutation = usePostApiV1ExamImportExamItemBatch();

  async function handleImport() {
    try {
      const hasMultipleLangs =
        examCultureInfos.length > 1 && Object.keys(examLangIdByCultureInfo).length > 0;

      const items = hasMultipleLangs
        ? examCultureInfos
            .map(cultureInfo => {
              const langId = examLangIdByCultureInfo[cultureInfo];
              const ids = selectedQuestions
                .filter(q => q.cultureInfo === cultureInfo)
                .map(q => Number(q.id));
              return langId !== undefined && ids.length > 0
                ? { examLangId: langId, examItemIds: ids }
                : null;
            })
            .filter((item): item is { examLangId: number; examItemIds: number[] } => item !== null)
        : [
            {
              examLangId: examLangId,
              examItemIds: selectedQuestions.map(q => Number(q.id)),
            },
          ];

      if (items.length === 0) return;

      await importMutation.mutateAsync({ data: { items } });

      for (const item of items) {
        void queryClient.invalidateQueries({
          queryKey: getApiV1ExamLangsExamlangidItemsQueryKey(item.examLangId),
        });
      }

      alert({ alertType: 'success', title: t('toast.questionImported') });
      onImport(selectedQuestions);
      setSelectedQuestions([]);
    } catch {
      alert({ alertType: 'warning', title: t('toast.error') });
    }
  }

  function handleClose() {
    setSelectedQuestions([]);
    onClose();
  }

  return (
    // `modal={false}`: os dropdowns dos Autocomplete de filtro são portados ao
    // body (Headless UI Combobox). No modo modal o Radix aplica
    // `pointer-events: none` fora do Content e as opções aparecem mas não
    // clicam. Ver reference_ads_autocomplete_selecteditem.
    <Dialog.Root open={open} onOpenChange={o => !o && handleClose()} modal={false}>
      <Dialog.Portal>
        {/* Backdrop manual: com `modal={false}` o Radix não renderiza o Dialog.Overlay. */}
        <div
          aria-hidden="true"
          className="fixed inset-none z-50 h-screen w-screen bg-base-gray-900 bg-opacity-medium backdrop-blur-soft"
        />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[90vh] w-full max-w-5xl -translate-x-1/2 -translate-y-1/2 flex-col rounded-md bg-ctx-layout-surface shadow-level3 focus:outline-none"
          aria-describedby={undefined}
          // Clique no dropdown portado conta como "fora" e fecharia o modal.
          // Fecha só por X, Cancelar ou Esc.
          onInteractOutside={e => e.preventDefault()}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b-hairline border-ctx-layout-border px-md py-xs">
            <div className="flex items-center gap-xs">
              <Dialog.Title className="ts-subtitle-sm text-ctx-content-title">
                {t('library.importFromLibrary.title')}
              </Dialog.Title>
              {selectedQuestions.length > 0 && (
                <Tag
                  label={t('library.importFromLibrary.selected', {
                    count: selectedQuestions.length,
                  })}
                  status="highlight"
                  size="sm"
                  custom={{ text: 'text-nowrap' }}
                />
              )}
            </div>
            <Dialog.Close asChild>
              <IconButton
                icon={XMarkIcon}
                hierarchy="tertiary"
                size="sm"
                ariaLabel={t('actions.close')}
                onClick={handleClose}
              />
            </Dialog.Close>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-md py-sm at-scrollbar">
            <ExamQuestionSelectionStep
              examLangId={examLangId}
              examCultureInfos={examCultureInfos}
              {...(initialCultureInfo !== undefined ? { initialCultureInfo } : {})}
              onSelectionChange={setSelectedQuestions}
            />
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-sm border-t-hairline border-ctx-layout-border px-md py-xs">
            <Button
              hierarchy="tertiary"
              size="md"
              onClick={handleClose}
              disabled={importMutation.isPending}
            >
              {t('actions.cancel')}
            </Button>
            <Button
              hierarchy="primary"
              size="md"
              disabled={selectedQuestions.length === 0 || !isBalanced}
              isLoading={importMutation.isPending}
              onClick={() => void handleImport()}
            >
              {t('library.importFromLibrary.importBtn')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
